export interface CSSRulesArr {
  [index: number]: CSSRulesArr | string;
  length: number;
}

export const css = {
  /**
   * Adds a stylesheet to the document head
   *
   * @param id - ID of the stylesheet
   * @param rules - CSS rules to be added
   */
  addStylesheetRules(styleEl: HTMLStyleElement, rules: CSSRulesArr[]): void {
    const { sheet } = styleEl;
    if (!sheet) return;

    for (let i = 0; i < rules.length; i++) {
      let j = 1,
        rule = rules[i],
        selector = rule[0],
        propStr = "";

      if (Array.isArray(rule[1][0])) {
        rule = rule[1];
        j = 0;
      }

      for (let pl = rule.length; j < pl; j++) {
        const prop = rule[j];
        propStr += `${prop[0]}: ${prop[1]}${!!prop[2] ? " !important" : ""};\n`;
      }

      sheet.insertRule(`${selector}{${propStr}}`, sheet.cssRules.length);
    }
  },

  /**
   * Adds a stylesheet to the document head
   *
   * @param id - ID of the stylesheet
   * @param rules - CSS rules to be added
   */
  isValidHex: (color: string) => color && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color),

  /**
   *  Checks if a hex color is dark or light
   *
   * @param hexColor - Hex color code (with or without #)
   * @returns
   */
  isDarkColor(hexColor: string): boolean {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
    const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
    const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);

    const brightness = r * 0.299 + g * 0.587 + b * 0.114;

    return brightness < 128;
  },

  /**
   * Converts a hex color to an rgba color with an alpha value
   * @param hex - Hex color code (with or without #)
   * @param alpha - Alpha value (0 to 1)
   * @returns RGBA color string
   */
  hex2rgba(hex: string, alpha: number = 1) {
    const [r, g, b] = hex.match(/\w\w/g)!.map((x) => parseInt(x, 16));
    return `rgba(${r},${g},${b},${alpha})`;
  },

  /**
   * Checks if the background color is dark enough to use white text
   *
   * @param color - Hex color code (with or without #)
   * @returns
   */
  checkBackgroundColorHsp(color: any): boolean {
    const hex: number = +("0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&"));

    const red = hex >> 16;
    const green = (hex >> 8) & 255;
    const blue = hex & 255;

    const hsp = Math.sqrt(0.299 * (red * red) + 0.587 * (green * green) + 0.114 * (blue * blue));

    if (hsp >= 170) return false;

    return true;
  },

  /**
   * Darkens a hex color by a specified percentage
   * @param color - Hex color code (with or without #)
   * @param amount - Percentage to darken (0 to 100)
   * @returns Darkened hex color
   */
  darken(color: string, amount: number): string {
    if (!this.isValidHex(color)) {
      return color;
    }

    const hex = color.replace("#", "");
    const amt = Math.min(Math.max(amount, 0), 100) / 100;

    let r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
    let g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
    let b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);

    r = Math.round(r * (1 - amt));
    g = Math.round(g * (1 - amt));
    b = Math.round(b * (1 - amt));

    r = Math.min(Math.max(r, 0), 255);
    g = Math.min(Math.max(g, 0), 255);
    b = Math.min(Math.max(b, 0), 255);

    const rHex = r.toString(16).padStart(2, "0");
    const gHex = g.toString(16).padStart(2, "0");
    const bHex = b.toString(16).padStart(2, "0");

    return `#${rHex}${gHex}${bHex}`;
  },

  /**
   * Lightens a hex color by a specified percentage
   * @param color - Hex color code (with or without #)
   * @param amount - Percentage to lighten (0 to 100)
   * @returns Lightened hex color
   */
  lighten(color: string, amount: number): string {
    if (!this.isValidHex(color)) {
      return color;
    }

    const hex = color.replace("#", "");
    const amt = Math.min(Math.max(amount, 0), 100) / 100;

    let r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
    let g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
    let b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);

    r = Math.round(r + (255 - r) * amt);
    g = Math.round(g + (255 - g) * amt);
    b = Math.round(b + (255 - b) * amt);

    r = Math.min(Math.max(r, 0), 255);
    g = Math.min(Math.max(g, 0), 255);
    b = Math.min(Math.max(b, 0), 255);

    const rHex = r.toString(16).padStart(2, "0");
    const gHex = g.toString(16).padStart(2, "0");
    const bHex = b.toString(16).padStart(2, "0");

    return `#${rHex}${gHex}${bHex}`;
  },

  /**
   * Adjusts the lightness of a hex color
   * @param color - Hex color code (with or without #)
   * @param amount - Percentage to adjust (-100 to 100, negative darkens, positive lightens)
   * @returns Adjusted hex color
   */
  adjustLightness(color: string, amount: number): string {
    return amount < 0 ? this.darken(color, Math.abs(amount)) : this.lighten(color, amount);
  },
};
